<template>
  <KTCodePreview v-bind:title="'Danh sách nhóm nhà cung cấp'">
    <template v-slot:toolbar>
      <b-row>
        <b-button
          v-show="checkPermission('SUPPLIER_GROUP_INSERT')"
          variant="primary"
          size="sm"
          class="btn btn-primary font-weight-bolder btn-sm"
          @click="openCreateModal"
        >
          <i
            style="font-size: 1rem"
            class="flaticon2-add-1"
          ></i>Tạo mới
        </b-button>
      </b-row>
    </template>

    <template v-slot:preview>
      <b-row>
        <b-col cols="12">
          <b-row>
            <b-col
              cols="6"
              md="4"
              lg="3"
            >
              <b-input-group size="sm">
                <b-form-input
                  v-model="search"
                  placeholder="Tìm kiếm theo tên hoặc mã nhóm..."
                  @input="debounceSearch"
                ></b-form-input>
                <!-- <b-input-group-append>
                  <b-button
                    variant="outline-secondary"
                    @click="clearSearch"
                    v-if="search"
                  >
                    <i class="fa fa-times"></i>
                  </b-button>
                </b-input-group-append> -->
              </b-input-group>
            </b-col>
            <b-col
              cols="6"
              md="4"
              lg="3"
              class="mt-3 mt-md-0"
            >
              <b-form-select
                v-model="statusFilter"
                :options="statusOptions"
                size="sm"
                @change="fetchSupplierGroups"
                class="form-control-sm select-style"
              ></b-form-select>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-table
        :items="supplierGroups"
        :fields="fields"
        :busy="isLoading"
        responsive
        hover
        bordered
        show-empty
        empty-text="Không có dữ liệu"
        class="mt-7"
        @row-clicked="handleRowClicked"
        tbody-tr-class="cursor-pointer"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Đang tải...</strong>
          </div>
        </template>

        <template v-slot:cell(name)="row">
          <a
            v-if="checkPermission('SUPPLIER_GROUP_UPDATE')"
            href="#"
            @click.prevent="openEditModal(row.item)"
            class="text-primary font-weight-bold"
          >
            {{ row.item.name }}
          </a>
          <span v-else class="font-weight-bold">{{ row.item.name }}</span>
        </template>

        <template v-slot:cell(description)="row">
          <span :title="row.item.description">
            {{ truncateText(row.item.description, 50) }}
          </span>
        </template>

        <template v-slot:cell(providerCount)="row">
          <b-badge variant="info">
            {{ row.item.providerCount || 0 }}
          </b-badge>
        </template>

        <template v-slot:cell(createdAt)="row">
          {{ formatDate(row.item.createdAt) }}
        </template>

        <template v-slot:cell(actions)="row">
          <div
            v-if="checkPermission('SUPPLIER_GROUP_UPDATE') || checkPermission('SUPPLIER_GROUP_DELETE')"
            class="d-flex justify-content-center"
          >
            <b-dropdown
              size="sm"
              id="dropdown-left"
              no-caret
              right
            >
              <template slot="button-content">
                <i
                  style="font-size: 1rem; padding-right: 0px"
                  class="flaticon2-settings"
                ></i>
              </template>
              <b-dropdown-item @click="openEditModal(row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i
                    style="font-size: 1rem"
                    class="flaticon2-pen"
                  ></i>
                  &nbsp; Chỉnh sửa
                </span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="checkPermission('SUPPLIER_GROUP_DELETE')"
                @click="showDeleteAlert(row.item)"
                :disabled="isDeleting"
              >
                <span style="color: #3f4254; font-size: 12px">
                  <b-spinner
                    small
                    v-if="isDeleting && selectedSupplierGroupForDelete?.id === row.item.id"
                    class="mr-1"
                  ></b-spinner>
                  <i
                    v-else
                    style="font-size: 1rem; color: #d33"
                    class="flaticon2-rubbish-bin-delete-button"
                  ></i>
                  &nbsp; Xóa
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>

      <div
        v-if="totalPages > 1"
        class="d-flex justify-content-center"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalItems"
          :per-page="limit"
          @change="fetchSupplierGroups"
        ></b-pagination>
      </div>

      <!-- Create/Edit Modal -->
      <supplier-group-form-modal
        :show="showFormModal"
        :supplier-group="selectedSupplierGroup"
        :is-edit="isEdit"
        @close="closeFormModal"
        @supplier-group-saved="handleSupplierGroupSaved"
      />

      <!-- Excel Export Modal -->
      <supplier-group-export-modal
        ref="exportModal"
        :search-params="getSearchParams()"
        :total-items="totalItems"
        @export-success="handleExportSuccess"
      />

      <!-- Excel Import Modal -->
      <supplier-group-import-modal
        ref="importModal"
        @import-success="handleImportSuccess"
      />
    </template>
  </KTCodePreview>
</template>

<script>
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { makeToastFaile } from '@/utils/common';
import localData from '@/utils/saveDataToLocal';
import KTCodePreview from '@/view/content/CodePreview.vue';
import SupplierGroupFormModal from './SupplierGroupFormModal.vue';
import SupplierGroupExportModal from './components/SupplierGroupExportModal.vue';
import SupplierGroupImportModal from './components/SupplierGroupImportModal.vue';
import { SupplierGroupService } from './service/supplier-group.service';

export default {
  name: 'SupplierGroupList',
  components: {
    KTCodePreview,
    SupplierGroupFormModal,
    SupplierGroupExportModal,
    SupplierGroupImportModal,
  },
  data() {
    return {
      fields: [
        { key: 'name', label: 'Tên nhóm' },
        { key: 'code', label: 'Mã nhóm' },
        { key: 'description', label: 'Ghi chú' },
        { key: 'providerCount', label: 'Số lượng NCC', class: 'text-center' },
        { key: 'createdAt', label: 'Ngày tạo' },
        { key: 'actions', label: 'Thao tác', class: 'text-center', thStyle: { width: '80px' } },
      ],
      supplierGroups: [],
      totalItems: 0,
      totalPages: 0,
      currentPage: 1,
      limit: 20,
      search: '',
      statusFilter: null,
      statusOptions: [
        { text: 'Tất cả trạng thái', value: null },
        { text: 'Hoạt động', value: true },
        { text: 'Không hoạt động', value: false },
      ],
      isLoading: false,
      showFormModal: false,
      isEdit: false,
      selectedSupplierGroup: null,
      isDeleting: false,
      selectedSupplierGroupForDelete: null,
      searchTimeout: null,
    };
  },
  created() {
    this.fetchSupplierGroups();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Danh sách nhóm nhà cung cấp' },
    ]);
  },
  methods: {
    checkPermission: function (condition) {
      return localData.checkPermission(condition);
    },

    fetchSupplierGroups() {
      this.isLoading = true;

      const params = {
        page: this.currentPage,
        limit: this.limit,
        includeProviderCount: true,
      };

      if (this.search) {
        params.search = this.search;
      }

      if (this.statusFilter !== null) {
        params.isActive = this.statusFilter;
      }

      SupplierGroupService.getAll(params)
        .then((response) => {
          const responseData = response.data.data;
          this.supplierGroups = responseData.supplierGroups;
          this.totalItems = responseData.totalItems;
          this.totalPages = responseData.totalPages;
          this.currentPage = responseData.currentPage;
        })
        .catch((error) => {
          console.error('Error fetching supplier groups:', error);
          makeToastFaile(error.message || 'Lỗi khi tải dữ liệu');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchSupplierGroups();
      }, 500);
    },

    clearSearch() {
      this.search = '';
      this.fetchSupplierGroups();
    },

    openCreateModal() {
      this.isEdit = false;
      this.selectedSupplierGroup = {
        name: '',
        code: '',
        description: '',
      };
      this.showFormModal = true;
    },

    openEditModal(supplierGroup) {
      this.isEdit = true;
      this.selectedSupplierGroup = { ...supplierGroup };
      this.showFormModal = true;
    },

    closeFormModal() {
      this.showFormModal = false;
      this.selectedSupplierGroup = null;
    },

    handleSupplierGroupSaved() {
      this.closeFormModal();
      this.fetchSupplierGroups();
    },

    handleExport() {
      this.$refs.exportModal.showModal();
    },

    handleImport() {
      this.$refs.importModal.showModal();
    },

    handleExportSuccess() {
      // Optional: Add any post-export logic
    },

    handleImportSuccess() {
      this.fetchSupplierGroups();
    },

    getSearchParams() {
      const params = {
        page: this.currentPage,
        limit: this.limit,
      };

      if (this.search) {
        params.search = this.search;
      }

      if (this.statusFilter !== null) {
        params.isActive = this.statusFilter;
      }

      return params;
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength
        ? text.substring(0, maxLength) + '...'
        : text;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('vi-VN');
    },

    showDeleteAlert(supplierGroup) {
      this.$swal
        .fire({
          title: 'Xóa nhóm nhà cung cấp!',
          text: `Bạn có chắc muốn xóa nhóm nhà cung cấp "${supplierGroup.name}" này không?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: 'rgb(221, 51, 51)',
          cancelButtonColor: '#E5EAEE',
          confirmButtonText: 'Xóa',
          cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
          reverseButtons: true,
          showClass: {
            popup: 'animate__animated animate__fadeInDown',
          },
          hideClass: {
            popup: 'animate__animated animate__fadeOutUp',
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.deleteSupplierGroup(supplierGroup);
          }
        });
    },

    deleteSupplierGroup(supplierGroup) {
      this.isDeleting = true;
      this.selectedSupplierGroupForDelete = supplierGroup;

      SupplierGroupService.delete(supplierGroup.id)
        .then((response) => {
          const message =
            response.data.message || 'Xóa nhóm nhà cung cấp thành công!';
          this.makeToastSuccess(message);
          this.fetchSupplierGroups();
        })
        .catch((error) => {
          console.error('Error deleting supplier group:', error);
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            'Lỗi khi xóa nhóm nhà cung cấp';
          makeToastFaile(errorMessage);
        })
        .finally(() => {
          this.isDeleting = false;
          this.selectedSupplierGroupForDelete = null;
        });
    },

    handleRowClicked(row, column, event) {
      event.preventDefault();
      this.openEditModal(row);
    },

    makeToastSuccess(message) {
      const vm = this;
      vm.$bvToast.toast(message, {
        title: 'Thông báo',
        variant: 'success',
        solid: true,
      });
    },
  },
};
</script>
